'use client';

import { Upload, X } from 'lucide-react';
import React, { useCallback } from 'react';

interface ImageUploaderProps {
  currentImage: string | null;
  onImageSelected: (base64: string | null) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  currentImage, 
  onImageSelected 
}) => {
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size too large. Please upload an image under 5MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageSelected(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, [onImageSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageSelected(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, [onImageSelected]);

  if (currentImage) {
    return (
      <div className="relative w-full rounded-xl overflow-hidden border-2 border-gold-500/50 group bg-black">
        {/* eslint-disable-next-line @next/next/no-img-element -- Using base64 data URLs from user uploads */}
        <img 
          src={currentImage} 
          alt="Room Preview" 
          className="w-full h-auto max-h-[70vh] object-contain mx-auto" 
        />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button 
            onClick={() => onImageSelected(null)}
            className="bg-red-500/80 hover:bg-red-600 text-white px-6 py-3 rounded-full flex items-center gap-2 transition-transform transform hover:scale-105"
          >
            <X size={20} /> Remove & Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className="w-full aspect-video border-2 border-dashed border-stone-700 hover:border-gold-500/50 rounded-xl bg-stone-900/50 hover:bg-stone-900 transition-all flex flex-col items-center justify-center cursor-pointer group relative overflow-hidden"
    >
      <input 
        type="file" 
        accept="image/*" 
        onChange={handleFileChange} 
        className="absolute inset-0 opacity-0 cursor-pointer z-10"
      />
      
      <div className="p-8 text-center pointer-events-none">
        <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
          <Upload className="text-gold-500" size={32} />
        </div>
        <h3 className="text-xl font-medium text-white mb-2">Upload your room photo</h3>
        <p className="text-stone-400 max-w-sm mx-auto">
          Drag and drop or click to browse. <br/>
          <span className="text-sm opacity-60">Supports JPG, PNG (Max 5MB)</span>
        </p>
      </div>
    </div>
  );
};
